/**
 * taahhutnameYardimci.js - pure helpers behind the staff "Proje Durumu" panel (backlog #217): which uploaded files can be
 * the source of a taahhütname, how far the required ones are, the trail of one taahhütname, the electrician's form check.
 * No React, no services - tested in taahhutnameYardimci.test.js.
 *
 * The data is the body of GET /projects/{id}/taahhutnameler (#216 / #505, TaahhutnamelerDTO): `taahhutnameler[]` always
 * isg, elektrik, statik; each {key, label, required, state, satisfied, source, text, companyApproval, signedCopy, canApprove,
 * level, elektrikciImzasi, canRecordElektrikci}; `history[]` newest first; `statusId`; `uyarilar[]` after a write.
 */
import i18n, { currentLocaleTag } from "../../i18n";

// The source documents, as the backend reads them (util/TaahhutnameDurumu.of, ProjeGorselConstant, StandFileConstant):
// İSG <- one of the four Köşe renders under title 2, Elektrik <- the Kuşbakışı under title 2, Statik <- title 7 (no sub-files).
export const GORSEL_STAND_FILE_ID = 2;
export const STATIK_STAND_FILE_ID = 7;
export const KOSE_SUB_IDS = [81, 82, 83, 84];
export const KUSBAKISI_SUB_ID = 85;

/** Not-deleted uploads that the taahhütname `key` can be generated from, newest first. */
export function kaynakAdaylari(key, uploadedFiles) {
  const inSet = (f, subs) => subs.includes(Number(f.subStandFileId));
  const adaylar = (uploadedFiles || []).filter((f) => {
    if (!f || f.deleted) return false;
    if (key === "isg") return Number(f.standFileId) === GORSEL_STAND_FILE_ID && inSet(f, KOSE_SUB_IDS);
    if (key === "elektrik") return Number(f.standFileId) === GORSEL_STAND_FILE_ID && inSet(f, [KUSBAKISI_SUB_ID]);
    if (key === "statik") return Number(f.standFileId) === STATIK_STAND_FILE_ID;
    return false;
  });
  return adaylar.slice().sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0));
}

/** { toplam, tamam, hepsiTamam } over the REQUIRED taahhütnames; hepsiTamam is false when none is required. */
export function sartOzeti(data) {
  const gerekli = (data?.taahhutnameler || []).filter((t) => t.required);
  const tamam = gerekli.filter((t) => t.satisfied).length;
  return { toplam: gerekli.length, tamam, hepsiTamam: gerekli.length > 0 && tamam === gerekli.length };
}

/** The trail of one taahhütname, newest first (the backend already sends it that way; ties keep the order). */
export function gecmisOf(data, key) {
  return (data?.history || []).filter((h) => h.key === key);
}

const OLAYLAR = new Set([
  "SOURCE_CHOSEN", "SOURCE_UNCHOSEN", "COMPANY_APPROVED", "COMPANY_APPROVAL_RESET",
  "SIGNED_UPLOADED", "SIGNED_APPROVED", "SIGNED_REJECTED", "SIGNED_PENDING", "SIGNED_DELETED",
  "ELEKTRIKCI_IMZALADI", "ELEKTRIKCI_KALDIRILDI", "ELEKTRIKCI_SIFIRLANDI", "ELEKTRIKCI_FOTO",
]);

/** Locale key (projeler:taahhutnameDurumu.olay.<EVENT>) of a trail event; a new backend event falls back to `diger`. */
export function olayAnahtari(event) {
  return `taahhutnameDurumu.olay.${OLAYLAR.has(event) ? event : "diger"}`;
}

/** Locale key of the signature level badge (Elektrik only), or null before the decorator signs. */
export function seviyeAnahtari(level) {
  return level === "SADECE_DEKORATOR" || level === "DEKORATOR_ELEKTRIKCI" ? `taahhutnameDurumu.seviye.${level}` : null;
}

/** Badge color of the signature level: the panel's own (info once the decorator signed, success with the electrician too). */
export function seviyeRengi(level) {
  return level === "DEKORATOR_ELEKTRIKCI" ? "success" : "info";
}

// The dot on a list tile (StatusTile `marker`): not the badge's green, because the Elektrik tile itself is green by then.
const SEVIYE_NOKTASI = { SADECE_DEKORATOR: "#299cdb", DEKORATOR_ELEKTRIKCI: "#405189" };

/** Hex color of the tile dot for a signature level; null when there is no level (no dot). */
export function seviyeNoktasi(level) {
  return SEVIYE_NOKTASI[level] ?? null;
}

/** The level as text in the current language, for pages outside the `projeler` namespace; "" without a level. */
export function seviyeMetni(level) {
  const key = seviyeAnahtari(level);
  return key ? i18n.t(`projeler:${key}`) : "";
}

/**
 * Carries the #505 signature level (and the electrician's name) from the stand list's commitments[] onto commitments a page
 * builds another way (the company pages derive theirs from the uploaded files), matched by key. Others stay as they are.
 */
export function seviyeyiTasi(commitments, rosterCommitments) {
  const roster = new Map((rosterCommitments || []).map((c) => [c.key, c]));
  return (commitments || []).map((c) => {
    const r = roster.get(c.key);
    return r?.level ? { ...c, level: r.level, elektrikciName: r.elektrikciName ?? null } : c;
  });
}

/** Date and time for the panel; "—" without one. */
export function fmtZaman(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/**
 * The electrician's form: a kişi of the stand OR a typed name (the backend takes one of them), a date that is today or
 * earlier when given. Returns the refusal as a locale key, or null when the form can go.
 */
export function elektrikciFormHatasi({ personId, ad, imzaTarihi }, simdi = new Date()) {
  const kisi = personId != null && personId !== "";
  const yazilanAd = typeof ad === "string" ? ad.trim() : "";
  if (!kisi && !yazilanAd) return "taahhutnameDurumu.elektrikci.kisiGerekli";
  if (imzaTarihi) {
    const t = new Date(imzaTarihi);
    if (!Number.isFinite(t.getTime())) return "taahhutnameDurumu.elektrikci.tarihGecersiz";
    if (t.getTime() > simdi.getTime() + 24 * 60 * 60 * 1000) return "taahhutnameDurumu.elektrikci.tarihGelecek";
  }
  return null;
}

/** The fields the POST takes, trimmed, with the empty ones left out; a chosen kişi wins over a typed name. */
export function elektrikciGovdesi({ personId, ad, imzaTarihi, not, foto }) {
  const govde = {};
  if (personId != null && personId !== "") govde.personId = Number(personId);
  else if (typeof ad === "string" && ad.trim()) govde.ad = ad.trim();
  if (imzaTarihi) govde.imzaTarihi = imzaTarihi;
  if (typeof not === "string" && not.trim()) govde.not = not.trim();
  if (foto) govde.foto = foto;
  return govde;
}
